/**
 * WindTechTile.jsx (PR_RUN_036 — Wind Technology Phase 3)
 * ========================================================
 * Self-contained dashboard tile for the Wind Technology sector module.
 *
 * Data sources:
 *   GET  /api/wind-energy/dashboard-tile  — per-tenant aggregates
 *   POST /api/wind-energy/seed-topics     — admin-only seed CTA (empty state)
 *   GET  /api/wind-energy/glossary        — inline glossary drawer
 *
 * Behavior:
 *   • Loading shimmer while fetching the tile payload.
 *   • If the tenant has 0 seeded topics, shows an empty-state CTA "Seed
 *     wind library" (admin only — backend rejects non-admin/non-instructor).
 *   • Otherwise renders progress bar, seeded/expected stats, completion %,
 *     last-seeded relative time, and the regulatory framework anchor.
 *   • "View glossary" opens an inline drawer with the curated IEC 61400
 *     glossary fetched on demand.
 *
 * Visual chrome matches the existing dashboard KPI cards (deep-charcoal
 * card on top of `#111318`, 7% white border, rounded-xl).
 *
 * Authored: 2026-05-23 (E1, PR_RUN_036)
 */
import { useCallback, useEffect, useState } from "react";
import { Wind, ArrowRight, BookOpen, X, RefreshCw, AlertCircle, CheckCircle2 } from "lucide-react";
import { API } from "../App";
import { useAuth } from "../App";

const ACCENT = "#38BDF8"; // sky-blue — wind/sky semantic

const _relativeTime = (iso) => {
  if (!iso) return "Never";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "Never";
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return "Just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} h ago`;
  if (seconds < 86400 * 30) return `${Math.floor(seconds / 86400)} d ago`;
  return new Date(iso).toLocaleDateString();
};

const WindTechTile = () => {
  const { isAdmin } = useAuth();
  const [tile, setTile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const [glossary, setGlossary] = useState(null);
  const [glossaryLoading, setGlossaryLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API}/wind-energy/dashboard-tile`, { credentials: "include" });
      if (r.ok) {
        setTile(await r.json());
      } else {
        setError(`HTTP ${r.status}`);
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTile(); }, [fetchTile]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const r = await fetch(`${API}/wind-energy/seed-topics`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ auto_translate: false }),
      });
      if (r.ok) {
        await fetchTile();
      }
    } finally {
      setSeeding(false);
    }
  };

  const openGlossary = async () => {
    setGlossaryOpen(true);
    if (glossary) return;
    setGlossaryLoading(true);
    try {
      const r = await fetch(`${API}/wind-energy/glossary`, { credentials: "include" });
      if (r.ok) setGlossary(await r.json());
    } finally {
      setGlossaryLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-5" data-testid="wind-tech-tile-loading">
        <div className="flex items-center gap-2 mb-3">
          <Wind className="w-4 h-4 text-[#8B95A5] animate-pulse" />
          <span className="text-[#8B95A5] text-xs uppercase tracking-wide">Wind Technology</span>
        </div>
        <div className="h-6 w-32 bg-[#111318] rounded animate-pulse mb-3" />
        <div className="h-2 w-full bg-[#111318] rounded animate-pulse" />
      </div>
    );
  }

  if (error || !tile) {
    return (
      <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-5" data-testid="wind-tech-tile-error">
        <div className="flex items-center gap-2 mb-2">
          <Wind className="w-4 h-4 text-[#8B95A5]" />
          <span className="text-[#8B95A5] text-xs uppercase tracking-wide">Wind Technology</span>
        </div>
        <p className="text-sm text-[#8B95A5] flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-amber-400" />
          {error ? `Unable to load tile (${error})` : "No data available"}
        </p>
        <button
          onClick={fetchTile}
          className="mt-3 text-xs text-[#38BDF8] hover:underline flex items-center gap-1"
          data-testid="wind-tech-tile-retry"
        >
          <RefreshCw className="w-3 h-3" /> Retry
        </button>
      </div>
    );
  }

  const { seeded_topics, expected_topics, completion_pct, last_seeded_at_utc,
          regulatory_framework_anchor, regime_tag } = tile;
  const isEmpty = !seeded_topics;
  const isComplete = completion_pct >= 100;

  return (
    <>
      <div
        className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-5 transition-all hover:border-[rgba(56,189,248,0.25)]"
        data-testid="wind-tech-tile"
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[rgba(56,189,248,0.08)] flex items-center justify-center">
              <Wind className="w-4 h-4" style={{ color: ACCENT }} />
            </div>
            <div>
              <p className="text-white text-sm font-medium">Wind Technology</p>
              <p className="text-[#8B95A5] text-[10px] uppercase tracking-wide">{regime_tag}</p>
            </div>
          </div>
          {isComplete && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Complete
            </span>
          )}
        </div>

        {/* Empty state — admin can seed */}
        {isEmpty ? (
          <div className="mt-2" data-testid="wind-tech-tile-empty">
            <p className="text-[#8B95A5] text-xs mb-3">
              Wind technology library not yet seeded for this tenant.
              {expected_topics > 0 && (
                <span className="block mt-1">
                  Charstan ships <span className="text-white">{expected_topics} canonical IEC 61400 topics</span> ready to deploy.
                </span>
              )}
            </p>
            {isAdmin ? (
              <button
                onClick={handleSeed}
                disabled={seeding}
                className="w-full px-3 py-2 rounded-md text-xs font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: ACCENT }}
                data-testid="wind-tech-tile-seed-btn"
              >
                {seeding ? "Seeding library…" : `Seed ${expected_topics} wind topics`}
              </button>
            ) : (
              <p className="text-xs text-[#8B95A5] italic">Ask an admin to seed the wind library.</p>
            )}
          </div>
        ) : (
          <>
            {/* Progress */}
            <div className="mb-3">
              <div className="flex items-baseline justify-between mb-1.5">
                <div>
                  <span className="text-white font-bold text-2xl">{seeded_topics}</span>
                  <span className="text-[#8B95A5] text-sm"> / {expected_topics} topics</span>
                </div>
                <span className="text-sm font-mono" style={{ color: ACCENT }}>{completion_pct}%</span>
              </div>
              <div className="h-1.5 bg-[#111318] rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.min(completion_pct, 100)}%`,
                    background: isComplete ? "#10B981" : ACCENT,
                  }}
                />
              </div>
            </div>

            {/* Meta */}
            <div className="space-y-1.5 text-[11px] text-[#8B95A5] mb-3">
              <div className="flex justify-between gap-2">
                <span>Last refreshed</span>
                <span className="text-slate-300" data-testid="wind-tech-tile-last-seeded">
                  {_relativeTime(last_seeded_at_utc)}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span>Anchor</span>
                <span className="text-slate-300 text-right truncate" title={regulatory_framework_anchor}>
                  {regulatory_framework_anchor}
                </span>
              </div>
            </div>
          </>
        )}

        {/* Glossary CTA — always available (read-only) */}
        <button
          onClick={openGlossary}
          className="w-full mt-2 px-3 py-2 rounded-md text-xs font-medium text-[#38BDF8] border border-[rgba(56,189,248,0.25)] hover:bg-[rgba(56,189,248,0.08)] transition-colors flex items-center justify-center gap-1.5"
          data-testid="wind-tech-tile-glossary-btn"
        >
          <BookOpen className="w-3 h-3" />
          View glossary
          <ArrowRight className="w-3 h-3" />
        </button>
      </div>

      {/* Glossary drawer */}
      {glossaryOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6"
          onClick={() => setGlossaryOpen(false)}
          data-testid="wind-tech-glossary-drawer"
        >
          <div
            className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-[rgba(255,255,255,0.07)]">
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4" style={{ color: ACCENT }} />
                <h3 className="text-white font-semibold">Wind Energy Glossary</h3>
                {glossary?.count != null && (
                  <span className="text-[10px] text-[#8B95A5]">({glossary.count} terms)</span>
                )}
              </div>
              <button
                onClick={() => setGlossaryOpen(false)}
                className="text-[#8B95A5] hover:text-white"
                data-testid="wind-tech-glossary-close"
                aria-label="Close glossary"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {glossaryLoading ? (
                <p className="text-[#8B95A5] text-sm">Loading glossary…</p>
              ) : !glossary?.items?.length ? (
                <p className="text-[#8B95A5] text-sm">Glossary unavailable.</p>
              ) : (
                <dl className="space-y-3">
                  {glossary.items.map((term, idx) => (
                    <div key={idx} className="border-b border-[rgba(255,255,255,0.05)] last:border-b-0 pb-3 last:pb-0" data-testid={`wind-glossary-term-${idx}`}>
                      <dt className="text-white text-sm font-medium flex items-baseline gap-2">
                        <span>{term.term || term.name || term.abbrev}</span>
                        {term.abbrev && term.term && term.abbrev !== term.term && (
                          <span className="text-[10px] text-[#38BDF8] font-mono">{term.abbrev}</span>
                        )}
                      </dt>
                      <dd className="text-[#8B95A5] text-xs mt-1">
                        {term.definition || term.description || ""}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WindTechTile;
